import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const QUICK_LINKS = [
  { labelKey: "nav_home", to: "/" },
  { labelKey: "nav_routes", to: "/routes" },
  { labelKey: "nav_map", to: "/map" },
  { labelKey: "nav_for_business", to: "/business" },
  { labelKey: "footer_privacy", to: "/privacy" },
  { labelKey: "footer_terms", to: "/terms" },
];

const CATEGORY_LINKS = [
  { labelKey: "nav_cat_historical", to: "/category/historical" },
  { labelKey: "nav_cat_food", to: "/category/food" },
  { labelKey: "nav_cat_entertainment", to: "/category/entertainment" },
  { labelKey: "nav_cat_accommodation", to: "/category/accommodation" },
  { labelKey: "nav_cat_nature", to: "/category/nature" },
  { labelKey: "nav_cat_sightseeing", to: "/category/sightseeing" },
];

export default function FooterSection() {
  const { t } = useTranslation();

  return (
    <footer className="bg-anthracite-800 text-white">
      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        {/* Brand */}
        <div className="lg:col-span-1">
          <div className="flex items-center gap-2.5 mb-5">
            <div className="w-10 h-10 rounded-xl bg-forest-600 flex items-center justify-center">
              <span className="text-white font-display font-bold text-base">B</span>
            </div>
            <span className="font-display font-bold text-white text-xl">
              Bursa<span className="text-forest-400">Guide</span>
              <span className="text-stone-500 text-sm font-body font-normal">360</span>
            </span>
          </div>
          <p className="text-white/50 text-sm font-body leading-relaxed mb-6">
            {t("footer_desc")}
          </p>
          {/* Social */}
          <div className="flex items-center gap-3">
            {[
              { icon: "ri-instagram-line", href: "#" },
              { icon: "ri-facebook-circle-line", href: "#" },
              { icon: "ri-twitter-x-line", href: "#" },
              { icon: "ri-youtube-line", href: "#" },
            ].map((social) => (
              <a
                key={social.icon}
                href={social.href}
                rel="nofollow noopener"
                className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/8 hover:bg-forest-600 transition-colors cursor-pointer"
              >
                <i className={`${social.icon} text-base text-white/60 hover:text-white`} />
              </a>
            ))}
          </div>
        </div>

        {/* Quick Links */}
        <div>
          <h4 className="font-display font-bold text-white text-base mb-5">
            <a href="#links">{t("footer_links_title")}</a>
          </h4>
          <ul className="flex flex-col gap-3">
            {QUICK_LINKS.map((link) => (
              <li key={link.labelKey}>
                <Link
                  to={link.to}
                  className="text-white/50 hover:text-forest-400 text-sm font-body transition-colors cursor-pointer"
                >
                  {t(link.labelKey)}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Categories */}
        <div>
          <h4 className="font-display font-bold text-white text-base mb-5">
            <a href="#categories">{t("footer_categories_title")}</a>
          </h4>
          <ul className="flex flex-col gap-3">
            {CATEGORY_LINKS.map((link) => (
              <li key={link.labelKey}>
                <Link
                  to={link.to}
                  className="text-white/50 hover:text-forest-400 text-sm font-body transition-colors cursor-pointer"
                >
                  {t(link.labelKey)}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact + Newsletter */}
        <div>
          <h4 className="font-display font-bold text-white text-base mb-5">
            <a href="#contact">{t("footer_contact_title")}</a>
          </h4>
          <ul className="flex flex-col gap-3 mb-7">
            <li className="flex items-start gap-2.5">
              <div className="w-4 h-4 mt-0.5 flex items-center justify-center shrink-0">
                <i className="ri-map-pin-line text-forest-500 text-sm" />
              </div>
              <span className="text-white/50 text-sm font-body">{t("footer_address")}</span>
            </li>
            <li className="flex items-center gap-2.5">
              <div className="w-4 h-4 flex items-center justify-center shrink-0">
                <i className="ri-phone-line text-forest-500 text-sm" />
              </div>
              <a href={`tel:${t("footer_phone")}`} className="text-white/50 hover:text-white text-sm font-body transition-colors cursor-pointer">
                {t("footer_phone")}
              </a>
            </li>
            <li className="flex items-center gap-2.5">
              <div className="w-4 h-4 flex items-center justify-center shrink-0">
                <i className="ri-mail-line text-forest-500 text-sm" />
              </div>
              <a href={`mailto:${t("footer_email")}`} className="text-white/50 hover:text-white text-sm font-body transition-colors cursor-pointer">
                {t("footer_email")}
              </a>
            </li>
          </ul>

          {/* Newsletter */}
          <div>
            <p className="text-white text-sm font-semibold font-body mb-1">{t("footer_newsletter_title")}</p>
            <p className="text-white/40 text-xs font-body mb-3">{t("footer_newsletter_desc")}</p>
            <div className="flex gap-2">
              <input
                type="email"
                placeholder={t("footer_newsletter_placeholder")}
                className="flex-1 min-w-0 px-3 py-2 bg-white/8 border border-white/15 rounded-xl text-white placeholder:text-white/30 text-xs font-body outline-none focus:border-forest-500 transition-colors"
              />
              <button className="px-4 py-2 bg-forest-600 hover:bg-forest-500 text-white text-xs font-semibold font-body rounded-xl transition-colors cursor-pointer whitespace-nowrap">
                {t("footer_newsletter_btn")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/8">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-5 flex flex-col sm:flex-row items-center gap-3 justify-between">
          <p className="text-white/30 text-xs font-body text-center sm:text-left">{t("footer_rights")}</p>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="text-white/30 hover:text-white/60 text-xs font-body transition-colors cursor-pointer">
              {t("footer_privacy")}
            </Link>
            <Link to="/terms" className="text-white/30 hover:text-white/60 text-xs font-body transition-colors cursor-pointer">
              {t("footer_terms")}
            </Link>
            <Link to="/admin" className="text-white/20 hover:text-white/40 text-xs font-body transition-colors cursor-pointer">
              Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
